import { cn } from '@/lib/utils';
import type { ProjectStatus } from '../../../convex/lib/types';

interface ProjectStatusBadgeProps {
  status: ProjectStatus | string;
  className?: string;
}

/**
 * Small status chip. Editorial: hairline border, monospace label,
 * single colour cue per status. No coloured background blocks.
 */
const STATUS_META: Record<
  string,
  { label: string; tone: 'neutral' | 'progress' | 'awaiting' | 'success' | 'error' }
> = {
  draft: { label: 'draft', tone: 'neutral' },
  questionnaire: { label: 'questionnaire', tone: 'progress' },
  sketches: { label: 'sketches', tone: 'progress' },
  pipeline: { label: 'pipeline', tone: 'awaiting' },
  completed: { label: 'completed', tone: 'success' },
  failed: { label: 'failed', tone: 'error' },
};

const TONE_CLASS: Record<string, string> = {
  neutral: 'border-ink/25 text-graphite',
  progress: 'border-ink/40 text-ink',
  awaiting: 'border-ochre/60 text-ochre',
  success: 'border-moss/60 text-moss',
  error: 'border-vermilion/60 text-vermilion',
};

export function ProjectStatusBadge({ status, className }: ProjectStatusBadgeProps) {
  const meta = STATUS_META[status] ?? { label: status, tone: 'neutral' as const };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider',
        TONE_CLASS[meta.tone],
        className,
      )}
    >
      {meta.label}
    </span>
  );
}
